import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { Alert, App, Button, Input, Segmented, Typography } from 'antd';

import ContextBar from '../common/ContextBar';
import { DataState, Skeleton, deriveDataState } from '../loading';
import FormActionsFooter from '../common/FormActionsFooter';
import TokenInput from '../common/TokenInput';
import EscalationLadder from './EscalationLadder';
import NotificationPreviewRail from './NotificationPreviewRail';
import ResolveCriterion from './ResolveCriterion';
import ResponseButtons from './ResponseButtons';
import TriggerSentence, { ReadBack } from './TriggerSentence';
import { CardOptionLabel, buildMemberCardOptions } from '../cards/CardVisuals';
import { NotifyAudienceFields, useCollectionAudienceOptions } from '../collections/NotifyActionFields';
import { collectionCrumbs } from '../../utils/breadcrumbs';
import { resolveChannels } from '../../config/notificationChannels';
import { useCardDefinitions } from '../../contexts/CardDefinitionsContext';
import { useCollectionContext } from '../../contexts/CollectionContext';
import { useCurrentUser } from '../../contexts/CurrentUserContext';
import { useScreenShortcuts } from '../../contexts/ShortcutsContext';
import { useDirtyForm } from '../../hooks/useDirtyForm';
import { apiErrorMessage } from '../../utils/apiErrors';
import { buildEntryTokens, buildTriggerTokens } from '../../utils/automationTokens';
import { getCollectionFacetCardKeys, getCollectionMemberCardIds } from '../../utils/collections';
import { cardAndDescendantIds } from '../../utils/facets';
import { normalizeId } from '../../utils/ids';
import { buildFilterFields } from '../../utils/kqlBuilder';
import { userElementsOf } from '../../utils/userElements';
import { isAdmin } from '../../utils';
import { ARROW_COUNTERCLOCKWISE, LIST, TRASH } from '../icons';
import {
	createNotificationDef,
	deleteNotificationDef,
	getNotificationDef,
	updateNotificationDef,
} from '../../services/notifications';
import {
	CATEGORY_OPTIONS,
	SCOPES,
	SHAPES,
	TIER_OPTIONS,
	buildPayload,
	describeUnknownAudience,
	draftFromStored,
	findElementField,
	validateDraft,
} from './notificationEditorModel';
import { confirmDestructive } from '../common/confirmDestructive';
import { escapeBelongsElsewhere } from '../../utils/escapeOwner';
import useElementChoices from '../../hooks/useElementChoices';
import QueryRefusalPick from '../items/QueryRefusalPick';

const { Text } = Typography;

const SHAPE_PARAMS = { happens: SHAPES.HAPPENS, changes: SHAPES.CHANGES, approaches: SHAPES.APPROACHES };

// The query parameters the editor seeds from (below).
const SEED_PARAMS = ['shape', 'card', 'scope', 'from'];
const seedOf = search => {
	const params = new URLSearchParams(search || '');
	return SEED_PARAMS.map(name => params.get(name) || '').join('|');
};

/**
 * The route wrapper. The editor SEEDS from its query string — a shape, a card,
 * a tier, an inherited key to override — and seeding is a mount-time latch, so
 * a second launch has to mount a fresh form rather than reuse one whose
 * latches already fired. Keying the element on what it seeds from is how the
 * new-item route solves the same problem for templates; this is that rule,
 * applied to the same kind of launch.
 */
export function NotificationEditorRoute() {
	const { notificationId } = useParams();
	const { search } = useLocation();
	return <NotificationEditorPage key={`${notificationId || 'new'}|${seedOf(search)}`} />;
}

/**
 * What counts as leaving the editor, for the unsaved-changes guard: a change
 * of path, or a change to a query parameter the route keys the editor on —
 * that remounts it (above), so the typing goes as surely as on a rail link.
 * Any other query change keeps the same form on screen.
 */
export function leavesNotificationEditor({ currentLocation, nextLocation }) {
	if (!currentLocation || !nextLocation) return true;
	return currentLocation.pathname !== nextLocation.pathname
		|| seedOf(currentLocation.search) !== seedOf(nextLocation.search);
}

/**
 * THE notification editor: seven panels down the record form's main column and
 * a live preview in the side column.
 *
 * Not a modal, and the reason is structural rather than aesthetic. The two
 * automations editors are 760px antd Modals carrying four sections; this
 * carries seven, one of which is an inline-expanding ladder — a modal per rung
 * inside a modal — and the preview has to sit BESIDE the form rather than on
 * top of it, because the thing being previewed is the thing being edited. So
 * it takes the record-form layout: `item-form-main` at 660px of white panels,
 * the sticky 300px side column, one `ContextBar`, a `FormActionsFooter` at the
 * foot of the flow.
 *
 * The panels are in the order of the QUESTION each answers, which is the order
 * a person thinks in: what is this and who is it for · when does it fire · who
 * hears it · what does it say · when does it stop · what happens if nobody
 * answers · what can they do about it.
 *
 * Everything this editor does not render, it carries. A stored notification
 * holds `severity`, `description`, `channels`, `forkedFrom` and whatever a
 * later release adds; `notificationEditorModel` lifts those off and puts them
 * back on save, using `AutomationRuleEditor`'s mechanism rather than a second
 * one.
 */
export default function NotificationEditorPage() {
	const { collectionId, notificationId } = useParams();
	const [searchParams] = useSearchParams();
	const navigate = useNavigate();
	const { message, modal } = App.useApp();

	const isNew = !notificationId;
	const fromKey = searchParams.get('from') || '';

	const collection = useCollectionContext()?.collection || null;
	const { cards, getElementsForCard, loading: cardsLoading } = useCardDefinitions();
	const { user } = useCurrentUser();
	const platformAdmin = user ? (Array.isArray(user.roles) && user.roles.includes('admin')) : isAdmin();

	const [draft, setDraft] = useState(() => draftFromStored(null, {
		fromKey,
		scope: searchParams.get('scope') === 'user' ? SCOPES.USER : SCOPES.COLLECTION,
		shape: SHAPE_PARAMS[searchParams.get('shape')] || SHAPES.HAPPENS,
		cardId: normalizeId(searchParams.get('card')) || '',
	}));
	const [stored, setStored] = useState(null);
	// 'loading' | 'ready' | 'error' (the read failed: Retry) | 'missing' (the
	// API answered that it is not there, or not this person's). A saved
	// notification is never drawn, saved or deleted from the blank draft the
	// state starts as: nothing but 'ready' opens the form.
	const [loadState, setLoadState] = useState(isNew ? 'ready' : 'loading');
	const [loadAttempt, setLoadAttempt] = useState(0);
	const [formError, setFormError] = useState(null);
	const [saving, setSaving] = useState(false);
	const [previewToken, setPreviewToken] = useState(0);
	const savingRef = useRef(false);

	const { isDirty, resetDirty, allowNavigation, guardDiscard } = useDirtyForm(draft, {
		shouldBlock: leavesNotificationEditor,
	});
	const resetDirtyRef = useRef(resetDirty);
	useEffect(() => { resetDirtyRef.current = resetDirty; }, [resetDirty]);

	const listPath = `/collections/${collectionId}/form?tab=notifications`;
	const ready = loadState === 'ready' && !cardsLoading;

	useEffect(() => {
		if (isNew) return undefined;
		let active = true;
		setLoadState('loading');
		// Scoped by the collection, because the API has no single-definition GET:
		// the tier list is the read surface, and one request carries every tier
		// that reaches here (services/notifications.js).
		getNotificationDef(notificationId, { collectionId })
			.then(loaded => {
				if (!active) return;
				if (!loaded) throw Object.assign(new Error('Not found'), { response: { status: 404 } });
				const next = draftFromStored(loaded, { fromKey });
				setStored(loaded);
				setDraft(next);
				setLoadState('ready');
				resetDirtyRef.current(next);
			})
			.catch(error => {
				if (!active) return;
				// Only an answer is a verdict: a 404/403 is a notification that is not
				// there (or not theirs); no response, or a 5xx, is worth another try.
				setLoadState([403, 404].includes(error?.response?.status) ? 'missing' : 'error');
			});
		return () => { active = false; };
	}, [isNew, notificationId, collectionId, fromKey, loadAttempt]);

	/* ── The vocabulary every picker reads from ───────────────────────────── */

	const memberIds = useMemo(
		() => (collection ? getCollectionMemberCardIds(collection, cards) : new Set()),
		[collection, cards]
	);
	const cardOptions = useMemo(
		() => buildMemberCardOptions(cards || [], memberIds)
			.map(option => ({ ...option, label: <CardOptionLabel card={option.card} depth={option.depth} /> })),
		[cards, memberIds]
	);
	const elementsFor = useCallback(
		cardId => (cardId && getElementsForCard ? (getElementsForCard(cardId) || []) : []),
		[getElementsForCard]
	);
	const triggerElements = useMemo(() => elementsFor(draft.cardId), [elementsFor, draft.cardId]);

	// The filter vocabulary of ONE card and its descendants — an item of a
	// child card is an item of the parent, which is how the API expands
	// `card = X`. Memoised per card: the trigger and the resolution criterion
	// each ask for their own.
	const facetCardKeys = useMemo(() => getCollectionFacetCardKeys(collection), [collection]);
	const fieldsFor = useMemo(() => {
		const cache = new Map();
		return rawId => {
			const id = normalizeId(rawId);
			if (!id) return [];
			if (!cache.has(id)) {
				// A trigger is read where it fires (the API keeps its keys),
				// so the builder keeps them too (buildFilterFields `keepKeys`).
				cache.set(id, buildFilterFields({
					cards: cards || [],
					cardIds: new Set(cardAndDescendantIds(cards || [], id)),
					getElementsForCard,
					facetCardKeys,
					keepKeys: true,
				}));
			}
			return cache.get(id);
		};
	}, [cards, getElementsForCard, facetCardKeys]);
	const triggerFields = useMemo(() => fieldsFor(draft.cardId), [fieldsFor, draft.cardId]);

	const personal = draft.scope === SCOPES.USER;
	const { groups, users } = useCollectionAudienceOptions(collectionId, !personal);
	const triggerUserElements = useMemo(
		() => userElementsOf(triggerElements).map(el => ({ value: el.name, label: el.label?.singular || el.name })),
		[triggerElements]
	);
	// A threshold notice is still ABOUT a record — the vehicle whose
	// registration is expiring — so both shapes template the same way.
	// A reminder about each entry's date (KPR-836) names its entry too: the
	// `{{entry.*}}` vocabulary of the group whose dates it watches.
	const watchedGroup = useMemo(
		() => (draft.shape === SHAPES.APPROACHES && draft.entryGroup
			? (triggerElements || []).filter(el => el?.name === draft.entryGroup && el.dataType === 'repeating-group').pop() || null
			: null),
		[draft.shape, draft.entryGroup, triggerElements]
	);
	const tokens = useMemo(
		() => [...buildEntryTokens(watchedGroup), ...buildTriggerTokens(triggerElements, getElementsForCard)],
		[watchedGroup, triggerElements, getElementsForCard]
	);
	// The registry's own three cells, with the API's availability where the
	// catalog has told us and today's truth where it has not.
	const channels = useMemo(() => resolveChannels(null), []);

	/* ── Save, reset, delete ──────────────────────────────────────────────── */

	const update = useCallback(next => setDraft(current => ({ ...current, ...next })), []);

	const payloadOf = useCallback(() => buildPayload(draft, {
		isNew,
		collectionId: normalizeId(collectionId),
		field: draft.shape === SHAPES.CHANGES ? findElementField(triggerFields, draft.changedElement) : null,
	}), [draft, isNew, collectionId, triggerFields]);

	// An element a condition could mean several of (400 `ambiguous_element`,
	// element ids step 3): the person picks one, or any of these cards, and
	// the notification is saved again with the pick.
	// …answering exactly the conditions it was made for: any change forgets it.
	const elementPicks = useElementChoices(JSON.stringify([draft.cardId, draft.shape, draft.conditionKql, draft.becomes, draft.changedElement, draft.resolve]));

	const handleSave = async () => {
		if (!ready) return; // ⌘↵ before the record has loaded must not save the blank draft
		setFormError(null);
		const problem = validateDraft(draft);
		if (problem) { setFormError(problem); return; }
		if (savingRef.current) return; // ⌘↵ twice must not save twice
		savingRef.current = true;
		setSaving(true);
		try {
			const saved = isNew
				? await createNotificationDef(elementPicks.withChoices(payloadOf()))
				: await updateNotificationDef(notificationId, elementPicks.withChoices(payloadOf()));
			elementPicks.reset();
			message.success(isNew ? 'Notification created.' : 'Notification saved.');
			resetDirtyRef.current(draft);
			setStored(saved || stored);
			navigate(listPath);
		} catch (error) {
			// Verbatim: this endpoint's 400s are field-level and written for humans.
			if (!elementPicks.take(error)) setFormError(apiErrorMessage(error, 'Could not save the notification.'));
		} finally {
			savingRef.current = false;
			setSaving(false);
		}
	};

	/**
	 * Preview and "send me a test" read the SAVED row by id and take exactly one
	 * field from the client: WHICH COLLECTION to render it in, which only a
	 * global definition actually needs (defs.js `previewNotificationDef`). They
	 * are not handed the draft — the endpoints have never read one.
	 */
	const previewRequest = useCallback(
		() => ({ collection_id: normalizeId(collectionId) }),
		[collectionId]
	);

	const handleCancel = () => guardDiscard(() => navigate(listPath));

	/**
	 * Reset is a DELETE of this tier's row, so the cascade falls through to
	 * whatever is beneath it — the `TileLayoutModal` idiom verbatim. The row is
	 * dropped, never blanked: a row of empty values and no row at all must not
	 * mean two different things.
	 */
	const handleReset = () => ready && confirmDestructive(modal, {
		title: 'Go back to the inherited notification?',
		content: 'This collection\'s own version is removed and the one above it applies again. Nothing that already fired is affected. History can restore it.',
		okText: 'Reset',
		onOk: async () => {
			try {
				await deleteNotificationDef(notificationId);
				message.success('Reset to the inherited notification.');
				// The row is gone; what was typed into it goes with it.
				allowNavigation();
				navigate(listPath);
			} catch (error) {
				message.error(apiErrorMessage(error, 'That reset did not save.'));
			}
		},
	});

	const handleDelete = () => ready && confirmDestructive(modal, {
		title: `Delete “${draft.name || 'this notification'}”?`,
		content: 'It stops firing. Alerts already open stay open until they are resolved or expire. History can restore it.',
		okText: 'Delete',
		onOk: async () => {
			try {
				await deleteNotificationDef(notificationId);
				message.success('Notification deleted.');
				allowNavigation();
				navigate(listPath);
			} catch (error) {
				message.error(apiErrorMessage(error, 'That delete did not save.'));
			}
		},
	});

	useScreenShortcuts('Notification editor', [
		{ keys: ['mod', 'enter'], label: 'Save the notification' },
		{ keys: ['esc'], label: 'Cancel' },
	]);

	const shortcutRef = useRef(null);
	shortcutRef.current = { handleSave, handleCancel };
	useEffect(() => {
		const handler = event => {
			if ((event.metaKey || event.ctrlKey) && event.key === 'Enter') {
				event.preventDefault();
				shortcutRef.current.handleSave();
			} else if (event.key === 'Escape') {
				// An open dropdown, picker or dialog owns Escape first.
				if (escapeBelongsElsewhere(event)) return;
				event.preventDefault();
				shortcutRef.current.handleCancel();
			}
		};
		document.addEventListener('keydown', handler);
		return () => document.removeEventListener('keydown', handler);
	}, []);

	/* ── Render ───────────────────────────────────────────────────────────── */

	const inherited = Boolean(fromKey || stored?.forkedFrom);
	const title = draft.name.trim() || (isNew ? 'New notification' : 'Notification');
	const clean = !isNew && !isDirty;

	const tierOptions = TIER_OPTIONS
		.filter(option => option.value !== SCOPES.GLOBAL || platformAdmin)
		.map(option => ({ ...option, disabled: !isNew }));

	const formActions = (
		<>
			{clean ? <span className="item-form-draft">No changes</span> : null}
			<Button onClick={handleCancel}>Cancel</Button>
			<Button type="primary" onClick={handleSave} loading={saving} disabled={!ready || clean}>
				{isNew ? 'Create notification' : 'Save changes'}
			</Button>
		</>
	);

	const menuActions = [
		{ key: 'list', group: 'navigate', icon: <LIST />, label: 'Go to notifications', onClick: () => navigate(listPath) },
		!isNew && ready ? {
			key: 'delete',
			group: 'destructive',
			icon: <TRASH />,
			label: 'Delete notification…',
			onClick: handleDelete,
		} : null,
	].filter(Boolean);

	return (
		<div className="item-form-page notification-editor">
			<ContextBar
				breadcrumb={collectionCrumbs(collection, collectionId, 'Notifications')}
				record={collection}
				kind="collection"
				title={title}
				actions={(
					<>
						<Button onClick={() => setPreviewToken(token => token + 1)} disabled={isNew || !ready}>Preview</Button>
						{formActions}
					</>
				)}
				menuActions={menuActions}
			/>

			{formError ? (
				<Alert type="error" showIcon role="alert" className="notification-editor__error" message={formError} />
			) : null}
			<QueryRefusalPick
				inline
				className="notification-editor__error"
				refusal={elementPicks.refusal}
				disabled={saving}
				onPick={(value) => { elementPicks.pick(value); handleSave(); }}
				onRepick={() => { elementPicks.clearRefusal(); handleSave(); }}
			/>

			<div className="item-form-columns">
				<div className="item-form-main">
					{!ready ? (
						loadState === 'missing' ? (
							<Alert
								type="error"
								showIcon
								message="That notification is not available."
								description="It may have been deleted, or belong to someone else."
								action={<Button onClick={() => navigate(listPath)}>Go to notifications</Button>}
							/>
						) : (
							<DataState
								state={deriveDataState({
									loading: loadState === 'loading' || (loadState === 'ready' && cardsLoading),
									error: loadState === 'error' ? 'Check your connection and try again.' : null,
								})}
								skeleton={<Skeleton variant="form-field" count={5} />}
								error={loadState === 'error' ? 'Check your connection and try again.' : null}
								onRetry={() => setLoadAttempt(n => n + 1)}
								label="this notification"
							/>
						)
					) : (<>

						<section className="form-panel notification-editor__panel" data-testid="notification-editor-basics">
							<div className="micro-label form-panel__kicker">Basics</div>

							{inherited ? (
								<Alert
									type="info"
									showIcon
									className="notification-editor__inherited"
									message="This overrides a notification from a tier above"
									description="It keeps the same identity, so people who have already set their own delivery for it keep those settings. Reset it and the one above applies again."
								/>
							) : null}

							<div className="item-form-fields">
								<div className="item-form-field field--full">
									<label className="d-block" htmlFor="notification-name">Name</label>
									<Input
										id="notification-name"
										size="large"
										value={draft.name}
										placeholder="What this tells people, in a few words"
										onChange={event => update({ name: event.target.value })}
									/>
								</div>
								<div className="item-form-field field--full">
									<label className="d-block" id="notification-category-label">What kind of notification</label>
									{/* The catalogue's own grouping, and required on the API
									    side — five words, so a Segmented rather than a
									    select (platform-standards § 7b). It defaults to
									    Records, which is what the common notification is
									    about, so an author never has to think about it. */}
									<Segmented
										aria-labelledby="notification-category-label"
										value={draft.category}
										onChange={category => update({ category })}
										options={CATEGORY_OPTIONS}
									/>
									<div className="form-panel__hint">
										How it files in everyone's notification list — and what they filter by
										when they have a lot of them.
									</div>
								</div>
								<div className="item-form-field field--full">
									<label className="d-block" id="notification-tier-label">Who this is for</label>
									<Segmented
										aria-labelledby="notification-tier-label"
										value={draft.scope}
										onChange={scope => update({ scope })}
										options={tierOptions}
									/>
									<div className="form-panel__hint">
										{isNew
											// The one control that is three gates at once: the
											// tier it is stored at, whether `me` is legal, and
											// whether there is an audience to pick at all.
											? (personal
												? 'A personal notification reaches you and nobody else, and may ask about “me”.'
												: 'Everyone this reaches can still turn a channel off for themselves, unless it is locked.')
											: 'A notification\'s tier is fixed once it exists. To move it, make a new one at the tier you want.'}
									</div>
								</div>
							</div>

							{inherited ? (
								<div className="notification-editor__panel-footer">
									<Button
										danger
										icon={<ARROW_COUNTERCLOCKWISE />}
										onClick={handleReset}
										disabled={isNew}
									>
										Reset to inherited
									</Button>
									{isNew ? (
										<Text type="secondary">Nothing to reset yet — this override has not been saved.</Text>
									) : null}
								</div>
							) : null}
						</section>

						<TriggerSentence
							draft={draft}
							onChange={setDraft}
							cardOptions={cardOptions}
							elements={triggerElements}
							fields={triggerFields}
							collectionId={collectionId}
							allowMe={personal}
							userOptions={users}
						/>

						{personal ? (
							<section className="form-panel notification-editor__panel" data-testid="notification-editor-audience">
								<div className="micro-label form-panel__kicker">Who hears it</div>
								{/* Not a disabled picker: there is no choice here to
								    make. A personal notification has exactly one
								    recipient, who is also its author, and that is what
								    makes the tier safe (Decision 8). */}
								<Text type="secondary">
									You, and nobody else. A personal notification can only ever reach the person
									who wrote it — that is what lets it ask about “me”.
								</Text>
							</section>
						) : draft.notify?.unknown ? (
							<section className="form-panel notification-editor__panel" data-testid="notification-editor-audience">
								<div className="micro-label form-panel__kicker">Who hears it</div>
								{/* ⚠ NOT A PICKER SHOWING THE WRONG ANSWER. The API's
								    audience registry is wider than this editor's four
								    controls — `element-path` walks lookups to "the
								    manager" — and drawing the nearest thing we do have
								    would save it back as that, turning one person into
								    the whole collection. So it reads, and it carries
								    (notificationEditorModel § EDITOR_AUDIENCE_TYPES). */}
								<Alert
									type="info"
									showIcon
									className="notification-editor__inherited"
									message="This one reaches an audience this editor does not draw"
									description={<ReadBack text={describeUnknownAudience(draft.notify.unknown)} />}
								/>
							</section>
						) : (
							<section className="form-panel notification-editor__panel" data-testid="notification-editor-audience">
								<div className="micro-label form-panel__kicker">Who hears it</div>
								<NotifyAudienceFields
									draft={draft.notify}
									onChange={notify => update({ notify })}
									groups={groups}
									users={users}
									userElements={draft.shape === SHAPES.APPROACHES ? null : triggerUserElements}
									label={null}
								/>
								<div className="form-panel__hint">
									Everyone chosen here is still filtered by what they can actually see — nobody
									is notified about a record they have no access to.
								</div>
							</section>
						)}

						<section className="form-panel notification-editor__panel" data-testid="notification-editor-words">
							<div className="micro-label form-panel__kicker">What it says</div>
							<div className="notification-editor__words">
								<label className="d-block">Title</label>
								<TokenInput
									value={draft.title}
									tokens={tokens}
									placeholder="Suzy has no med administration today"
									ariaLabel="Notification title"
									onChange={value => update({ title: value })}
								/>
							</div>
							<div className="notification-editor__words">
								<label className="d-block">Message</label>
								<TokenInput
									multiline
									value={draft.body}
									tokens={tokens}
									placeholder="Optional detail"
									ariaLabel="Notification message"
									onChange={value => update({ body: value })}
								/>
							</div>
						</section>

						<ResolveCriterion
							draft={draft}
							onChange={setDraft}
							cardOptions={cardOptions}
							elementsFor={elementsFor}
							fieldsFor={fieldsFor}
							collectionId={collectionId}
							watchedGroup={watchedGroup}
						/>

						<EscalationLadder
							steps={draft.steps}
							onChange={steps => update({ steps })}
							channels={channels}
							tokens={tokens}
							groups={groups}
							users={users}
							userElements={draft.shape === SHAPES.APPROACHES ? null : triggerUserElements}
							unavailableHintId="notification-editor-channels-hint"
							// Invariant 2 reaches the rungs too: a personal ladder waits
							// longer and tries again, and has nobody else to hand it to.
							personal={personal}
						/>

						<ResponseButtons
							responses={draft.responses}
							onChange={responses => update({ responses })}
							cardOptions={cardOptions}
						/>

						<FormActionsFooter>{formActions}</FormActionsFooter>
					</>)}
				</div>

				<aside className="item-form-side">
					<NotificationPreviewRail
						notificationId={isNew ? null : notificationId}
						buildRequest={previewRequest}
						refreshToken={previewToken}
						unavailableReason={isNew
							? 'Save it once and the preview renders against your real records — who it reaches, and who it leaves out.'
							: null}
					/>

					<section className="form-panel">
						<div className="micro-label form-panel__kicker">How it reads</div>
						<ReadBack
							className="notification-editor__readback"
							text={'Every notification writes an inbox row whichever channels are on. The switches on the list govern what LEAVES the app — which is why all three off reads “Inbox only” rather than pretending somebody muted it.'}
						/>
					</section>

					<div className="item-form-shortcuts">
						<span><kbd>⌘↵</kbd> Save</span>
						<span><kbd>Esc</kbd> Cancel</span>
					</div>
				</aside>
			</div>
		</div>
	);
}
